import '@ezugi/analytics/patchDataDogLogger';
import '@ezugi/analytics/patchDataLayerPush.ts';
import { CacheProvider, Global } from '@emotion/core';
import { cache } from 'emotion';
import qs from 'qs';
import { path } from 'ramda';
import {
  ComponentType,
  Fragment,
  ReactNode,
  Suspense,
  useEffect,
  useState,
} from 'react';
import { Provider, useSelector } from 'react-redux';

import { initializeDataDog } from '@ezugi/analytics/initializeDataDog';
import { ControlPanel } from '@ezugi/control-panel';
import { setTheme } from '@ezugi/ui/setTheme';
import { DeviceInfo, isEvoWrapped, useLogDevicePerformance } from '@ezugi/utils';

import 'normalize.css';
import { FlyingChips } from './components/ChipSelector/FlyingChips';
import { useF11Fullscreen } from './hooks/useFullscreen';
import useFullscreenOnTouch from './hooks/useFullscreenOnTouch';
import useRotateViewportSizeVariables from './hooks/useRotateViewportSizeVariables';
import { isAuthenticatedSelector } from './store/selectors';
import { errorSelector } from './store/selectors/error';
import { evoGlobalStyle, globalStyle } from './styles';
import { setViewportSizeVariables } from './util/viewport';
import GameError from './views/GameError';

// TODO: https://ezugi1.atlassian.net/browse/ERD-15299
declare const window: {
  __TAG_VERSION?: string
} & Window;

const App = ({ ErrorComponent, children }: {ErrorComponent?: ComponentType, children : ReactNode}) => {
  const error = useSelector(errorSelector);
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const preloaderConfig = useSelector(path([ 'config', 'preloader' ]));

  useF11Fullscreen();
  useFullscreenOnTouch();
  useRotateViewportSizeVariables();
  useLogDevicePerformance();

  const loadGame = !error.code && preloaderConfig && isAuthenticated;

  if (error.code) {
    return ErrorComponent ? <ErrorComponent /> : <GameError />;
  }

  return loadGame
    ? (
      <Suspense fallback={null}>
        {children}
      </Suspense>
    )
    : null;
};

type GameShellProps = {
  store: any;
  InjectGlobal?: ComponentType;
  ErrorComponent?: ComponentType;
  children?: ReactNode;
};

const { search } = window.location;
const params = qs.parse(search.substr(1));

setTheme();

cache.compat = true;

initializeDataDog();

/**
 * Add global variables for app & tag version
 * Replaced by Vite at build time
 */

// @ts-ignore
// eslint-disable-next-line no-undef
window.__VERSION = __VERSION;
// @ts-ignore
// eslint-disable-next-line no-undef
window.__TAG_VERSION = __TAG_VERSION;

export const GlobalStyleMount = () => {
  useEffect(() => {
    if (DeviceInfo.isHandheld) {
      setViewportSizeVariables();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Fragment>

      <Global styles={globalStyle} />
      {isEvoWrapped() && <Global styles={evoGlobalStyle} />}
    </Fragment>
  );
};

const FontsHack = () => (
  <Fragment>
    <span
      style={{
        opacity: 0,
        width: 0,
        height: 0,
        pointerEvents: 'none',
        visibility: 'hidden',
        fontFamily: 'Roboto',
        fontWeight: 'bold',
        position: 'fixed',
      }}
    >
      Hack for loading fonts
    </span>

    {/* Preload font for reconnecting blur screen */}
    <span
      style={{
        opacity: 0,
        width: 0,
        height: 0,
        pointerEvents: 'none',
        visibility: 'hidden',
        fontFamily: 'Montserrat',
        fontWeight: 'bold',
        position: 'fixed',
      }}
    >
      Hack for loading fonts
    </span>
  </Fragment>
);

const GameShell = ({ store, InjectGlobal, ErrorComponent, children }: GameShellProps) => {
  const [ shouldRender, setShouldRender ] = useState(false);

  const runAuth = async () => import('./auth').then(({ auth }) => auth()).then(() => {
    setShouldRender(true);
  });

  useEffect(() => {
    if ('operatorId' in params && 'token' in params && !('identifier' in params)) {
      runAuth();
    } else {
      setShouldRender(true);
    }
  }, []);

  if (!shouldRender) {
    return null;
  }

  return (
    <Provider store={store}>
      <CacheProvider value={cache}>
        <GlobalStyleMount />
        <Suspense fallback={null}>
          {InjectGlobal ? <InjectGlobal /> : null}
        </Suspense>
        <App ErrorComponent={ErrorComponent}>
          {children}
        </App>
      </CacheProvider>
      <FontsHack />
      <FlyingChips />

      {import.meta.env.VITE_CONTROL_PANEL && <ControlPanel />}
    </Provider>
  );
};

export default GameShell;
